import { createContext, useContext } from "react"
import { apiBaseUrl } from "@/lib/utils"

// ENG-1476: helpers for the LMS launch window (app.ambr.ai/lms/launch), the
// popup a SCORM package opens from a customer's LMS. The window holds a
// session limited to one roleplay attempt, kept in sessionStorage so it never
// leaks into other tabs or outlives the window.

// Set by the inline script in index.html from the URL fragment, before any
// analytics or replay script can record the URL
export const lmsLaunchTokenStorageKey = "ambr:lmsLaunchToken"
export const lmsSessionTokenStorageKey = "ambr:lmsSessionToken"

// Whether this window is the LMS launch window
export function isLmsWindow() {
  return (
    window.location.pathname === "/lms" ||
    window.location.pathname.startsWith("/lms/")
  )
}

function readSessionStorage({ key }: { key: string }) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSessionStorage({
  key,
  value,
}: {
  key: string
  value: string | null
}) {
  try {
    if (value === null) {
      sessionStorage.removeItem(key)
    } else {
      sessionStorage.setItem(key, value)
    }
  } catch {
    // sessionStorage can be unavailable (privacy modes); the window then works
    // until reloaded
  }
}

// The scoped session token, only ever used inside the LMS window
export function getLmsSessionToken() {
  return isLmsWindow()
    ? readSessionStorage({ key: lmsSessionTokenStorageKey })
    : null
}

export function setLmsSessionToken({ token }: { token: string | null }) {
  writeSessionStorage({ key: lmsSessionTokenStorageKey, value: token })
}

// Takes (and forgets) the launch token the inline script stashed
export function takeLmsLaunchToken() {
  const token = readSessionStorage({ key: lmsLaunchTokenStorageKey })
  writeSessionStorage({ key: lmsLaunchTokenStorageKey, value: null })
  return token
}

// What the launch window shows for the current attempt (mirrors the API)
export type LmsSessionState =
  | {
      state: "ready"
      launchId: string
      userId?: number
      scenarioId?: string
      difficultyLevel?: string | null
      orgName?: string
      resultFrozen?: boolean
      resultCallId?: string | null
      // Calls this launch can still start (restarts and try-agains)
      callsRemaining?: number
    }
  | {
      state: "verify"
      launchId?: string
      suggestedEmail: string | null
      codeSent: boolean
      // An earlier email isn't an Ambr user yet and the admin has been asked
      waitingForAdmin?: boolean
    }
  | { state: "pending-admin"; launchId?: string }
  | {
      state: "blocked"
      launchId?: string
      reason: "licence" | "contact-admin" | "unavailable"
      learnerId: string | null
    }

// Calls a public /lms endpoint. Returns the status and parsed body; never
// throws (HTTP errors keep their status, network failures return status 0)
// so the window can always show a specific message.
export async function lmsRequest<T>({
  path,
  method = "GET",
  body,
}: {
  path: string
  method?: "GET" | "POST"
  body?: unknown
}) {
  const sessionToken = readSessionStorage({ key: lmsSessionTokenStorageKey })
  try {
    const response = await fetch(`${apiBaseUrl}/lms${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(sessionToken ? { "x-lms-session": sessionToken } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    const data = (await response.json().catch(() => ({}))) as T
    return { status: response.status, data }
  } catch {
    // Couldn't reach Ambr at all (offline, blocked). Status 0 lets callers
    // offer a retry instead of getting stuck.
    return { status: 0, data: { code: "network_error" } as T }
  }
}

// Context that switches the roleplay and feedback screens into LMS mode:
// no navigation into the wider app, no sharing, history or podcasts, and
// hand-offs back to the launch window instead of route changes
export const LmsModeContext = createContext<{
  launchId: string
  // The roleplay finished and its call is ready to show
  onCallReady: (args: { callId: string }) => void
  // Feedback for the call is showing (the LMS result may now be frozen)
  onFeedbackShown: () => void
  // Calls this launch can still start, and going back to start another one
  // after a call that didn't count (too short, too little dialogue)
  callsRemaining: number
  onTryAgain: () => void
} | null>(null)

export function useLmsMode() {
  return useContext(LmsModeContext)
}

// Saves a base64 zip returned by the API as a file download
export function downloadBase64Zip({
  base64,
  fileName,
}: {
  base64: string
  fileName: string
}) {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/zip" }),
  )
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
